import {useState} from 'react'
import {ScrollView, View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  OnboardingControls,
  OnboardingDescriptionText,
  OnboardingPosition,
  OnboardingTitleText,
} from '#/screens/Onboarding/Layout'
import {useOnboardingInternalState} from '#/screens/Onboarding/state'
import {
  CATEGORY_LABELS,
  getCreatorTopicsCopy,
  getFilteredTopics,
  getLearnerTopicsCopy,
  getOrgTopicsCopy,
  MAX_TOPICS,
  resolveTopicsLevel,
  TOPIC_ICONS,
  TOPIC_LABELS,
} from '#/screens/Onboarding/StepGoal/data'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Toggle from '#/components/forms/Toggle'
import {Text} from '#/components/Typography'

export function StepTopics() {
  const {i18n, t: l} = useLingui()
  const t = useTheme()
  const {state, dispatch} = useOnboardingInternalState()

  const g = state.goalStepResults
  const accountType = g.accountType ?? 'learner'
  const level = resolveTopicsLevel(g, 'goal-topics')
  const excludeCategories =
    accountType === 'learner' ? ['Scholarly Subjects'] : []
  const filteredTopics = getFilteredTopics(level, excludeCategories)
  const max = MAX_TOPICS[accountType] ?? 5

  const {title, sub: description} =
    accountType === 'learner'
      ? getLearnerTopicsCopy(g.learningStage, max)
      : accountType === 'creator'
        ? getCreatorTopicsCopy(g.creatorRole, g.audience, max)
        : getOrgTopicsCopy(g.orgType, g.audience, max)

  const [topics, setTopics] = useState<string[]>(g.topics)

  function onChange(vals: string[]) {
    if (vals.length <= max) setTopics(vals)
  }

  function onContinue() {
    dispatch({type: 'setGoalResults', results: {topics}})
    dispatch({type: 'next'})
  }

  return (
    <View style={[a.align_start, a.gap_sm]} testID="onboardingTopics">
      <OnboardingPosition />
      <OnboardingTitleText>{title}</OnboardingTitleText>
      <OnboardingDescriptionText>{description}</OnboardingDescriptionText>

      <ScrollView
        style={[a.w_full, {maxHeight: 340}]}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled>
        <View style={[a.pt_lg, a.gap_lg]}>
          <Toggle.Group
            values={topics}
            onChange={onChange}
            label={l`Select topics`}>
            {Object.entries(filteredTopics).map(([cat, items]) => (
              <View key={cat} style={[a.gap_sm, a.mb_md]}>
                <Text
                  style={[
                    a.text_sm,
                    a.font_bold,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {CATEGORY_LABELS[cat] ? i18n._(CATEGORY_LABELS[cat]) : cat}
                </Text>
                <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
                  {items.map(item => {
                    const isDisabled =
                      topics.length >= max && !topics.includes(item)
                    return (
                      <Toggle.Item
                        key={item}
                        name={item}
                        label={
                          TOPIC_LABELS[item] ? i18n._(TOPIC_LABELS[item]) : item
                        }>
                        {({selected}) => (
                          <View
                            style={[
                              a.px_md,
                              a.py_sm,
                              a.rounded_full,
                              a.border,
                              selected
                                ? {
                                    borderColor: 'transparent',
                                    ...t.atoms.bg_contrast_975,
                                  }
                                : t.atoms.border_contrast_low,
                              isDisabled && {opacity: 0.4},
                            ]}>
                            <Text
                              style={[
                                a.text_sm,
                                a.font_bold,
                                selected && t.atoms.text_inverted,
                              ]}
                              emoji>
                              {TOPIC_ICONS[item]}{' '}
                              {TOPIC_LABELS[item]
                                ? i18n._(TOPIC_LABELS[item])
                                : item}
                            </Text>
                          </View>
                        )}
                      </Toggle.Item>
                    )
                  })}
                </View>
              </View>
            ))}
          </Toggle.Group>
        </View>
      </ScrollView>

      <OnboardingControls.Portal>
        <Button
          disabled={topics.length === 0}
          testID="onboardingContinue"
          color="primary"
          size="large"
          label={l`Continue to next step`}
          onPress={onContinue}>
          <ButtonText>
            <Trans>Continue</Trans>
          </ButtonText>
        </Button>
      </OnboardingControls.Portal>
    </View>
  )
}
